import React from 'react';
import { useTranslation } from 'react-i18next';
import { observer } from 'mobx-react';
import { Controller, SubmitHandler, useFormContext } from 'react-hook-form';
import Grid from '@material-ui/core/Grid';
import ErrorIcon from '@material-ui/icons/Error';
import useModal from '@yaydoo/react-components/lib/hooks/useModal';

import useStore from '@hooks/useStore';
import Input from '@components/common/Input';
import MaskedInput from '@components/common/MaskedInput';
import PayButton from '@components/PayButton';
import { IRedPayPaymentData, PartnerFormProps } from '@containers/checkout/payment-form/types';
import { isCardNumberValid, isCVVNumberValid, isValidExpirationDate } from '@utils/validators';
import useStyles from './styles';
import { FieldValues } from 'react-hook-form/dist/types';
import { Partner } from '@constants/paymentLink';
import FormPartnerLogo from '@components/FormPartnerLogo';
import { getCardType } from '@utils/openPay';
import { getCardIcon } from '@utils/cardHelper';

const RedPayCardForm: React.FC<PartnerFormProps> = ({ onValidationError }) => {
  const css = useStyles();
  const {
    redPay,
    checkout,
    links: {
      paymentLink: { paymentLinkId, total: amount, isSubscription },
      paymentConfig: {
        paymentMethods: { card },
      },
    },
  } = useStore();
  const { merchantId, publicApiKey } = card;
  const { t } = useTranslation();
  const { control, formState, handleSubmit, watch } = useFormContext();
  const cardProvider = getCardType(watch('cardNumber') || '');
  const CardProviderIcon = getCardIcon(cardProvider);

  const CardPaymentError: React.FC = () => (
    <Grid item className={css.paymentError}>
      <ErrorIcon />{' '}
      {redPay?.errors?.card?.message !== ''
        ? redPay?.errors?.card?.message
        : t('checkoutScreen.payment.errors.card.unknown')}
    </Grid>
  );

  const pay = (values: IRedPayPaymentData) => {
    const data: IRedPayPaymentData = {
      ...values,
      paymentLinkId,
      amount: +amount,
    };
    redPay.processCardPayment(data as IRedPayPaymentData, merchantId, publicApiKey);
  };

  return (
    <>
      <Grid container spacing={2}>
        {redPay.hasCardProcessErrors && <CardPaymentError />}
        <Grid item xs={12}>
          <Controller
            name="cardHolderName"
            control={control}
            defaultValue=""
            rules={{ required: true }}
            render={({ field, fieldState }) => (
              <Input
                {...field}
                data-testid="cardholder-name"
                fullWidth
                autoComplete="off"
                label={t('checkoutScreen.payment.methods.card.name')}
                disabled={formState.isSubmitting}
                error={fieldState.invalid || redPay.hasCardProcessErrors}
              />
            )}
          />
        </Grid>

        <Grid item xs={12}>
          <Controller
            name="cardNumber"
            control={control}
            defaultValue={false}
            rules={{ required: true, validate: { card: isCardNumberValid } }}
            render={({ field, fieldState }) => (
              <MaskedInput
                fullWidth
                data-testid="payment-number"
                label={t('checkoutScreen.payment.methods.card.number')}
                autoComplete="off"
                inputProps={{
                  inputMode: 'numeric',
                  guide: true,
                  mask: '9999 9999 9999 9999',
                }}
                onChange={(e) => {
                  field.onChange(e.target.value.replace(/\s/g, ''));
                }}
                disabled={formState.isSubmitting}
                error={fieldState.invalid || redPay.hasCardProcessErrors}
                InputProps={{
                  endAdornment: CardProviderIcon && <CardProviderIcon />,
                }}
              />
            )}
          />
        </Grid>

        <Grid item xs={6}>
          <Controller
            name="cardExpiration"
            control={control}
            defaultValue={false}
            rules={{
              required: true,
              validate: { expiration: isValidExpirationDate },
            }}
            render={({ field, fieldState }) => (
              <MaskedInput
                fullWidth
                data-testid="payment-card-expires"
                label={t('checkoutScreen.payment.methods.card.validity')}
                autoComplete="off"
                placeholder={t('checkoutScreen.payment.methods.card.validityPlaceholder')}
                inputProps={{
                  inputMode: 'numeric',
                  guide: true,
                  mask: '99/99',
                }}
                onChange={(e) => {
                  field.onChange(e.target.value);
                }}
                disabled={formState.isSubmitting}
                error={fieldState.invalid || redPay.hasCardProcessErrors}
              />
            )}
          />
        </Grid>

        <Grid item xs={6}>
          <Controller
            name="cardCvv"
            control={control}
            defaultValue={false}
            rules={{ required: true, validate: { cvv: isCVVNumberValid } }}
            render={({ field, fieldState }) => (
              <MaskedInput
                fullWidth
                data-testid="payment-cvv"
                type="password"
                label={t('checkoutScreen.payment.methods.card.cvv')}
                autoComplete="off"
                inputProps={{
                  inputMode: 'numeric',
                  mask: '9999',
                  className: css.cvv,
                }}
                onChange={(e) => {
                  field.onChange(e.target.value);
                }}
                disabled={formState.isSubmitting}
                error={fieldState.invalid || redPay.hasCardProcessErrors}
              />
            )}
          />
        </Grid>

        <Grid item xs={6} hidden={!checkout.isZipCodeRequired}>
          <Input
            fullWidth
            data-testid="payment-zip-code"
            label={t('checkoutScreen.payment.methods.card.zipCode')}
            autoComplete="off"
            inputProps={{ maxLength: 6 }}
            value={redPay.card.zipCode}
            onChange={(e) => {
              redPay.updateField('card.zipCode', e.target.value);
            }}
            disabled={redPay.isProcessing}
            error={redPay.errors.card.zipCode}
          />
        </Grid>
      </Grid>
      <PayButton
        onClick={handleSubmit(pay as SubmitHandler<FieldValues>, onValidationError)}
        disabled={redPay.isProcessing || !checkout.isAllowedToPayWithCard}
        loading={redPay.isProcessing}
        isSubscription={isSubscription}
        label={
          !isSubscription
            ? t('checkoutScreen.payment.methods.submit')
            : t('checkoutScreen.payment.methods.subscribe')
        }
      />
      <FormPartnerLogo partner={Partner.REDPAY} />
    </>
  );
};

export default observer(RedPayCardForm);
